import { useEffect, useState, useContext } from 'react';
import { useForm, Controller } from "react-hook-form";
import { TextField, MenuItem, Select, ListSubheader, FormControl, InputLabel, ToggleButtonGroup, ToggleButton, } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import apiAdmin from '../../services/api-admin';
import { AdminUserContext } from '../../contexts/user-context';
import { MdOutlineAddHomeWork, MdOutlineRemoveCircleOutline } from "react-icons/md";

const CourtesySigningRequest = (props) => {
    const { adminUserProfile } = useContext(AdminUserContext);
    const { register, handleSubmit, control, reset, getValues, setValue, watch, clearErrors, setError, formState: { errors } } = useForm();
    const [regions, setRegions] = useState([]);
    const [pageError, setPageError] = useState(null);
    const [showSecondOffice, setShowSecondOffice] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [timeZone, setTimeZone] = useState('Mountain Time');
    const [transactionData, setTransactionData] = useState(null);

    useEffect(() => {
        const getRegions = async () => {
            apiAdmin.get(`/regions-with-offices`)
                .then((response) => {
                    setRegions(response.data);
                });
        };
        getRegions();
    }, []);

    const autoFillAddress = async () => {
        const fileNumber = getValues("fileNumber");
        if (!fileNumber) return;

        clearErrors("fileNumber");

        const response = await apiAdmin.get(`/get-transaction-by-file-number?fileNumber=${encodeURIComponent(fileNumber)}`,
            {
                validateStatus: (status) => status === 200 || status === 404
            }
        );

        if (response.status === 404) {
            setTransactionData(null);
            setValue("transactionType", "");
            setValue("lenderInformation", "");

            setError("fileNumber", {
                type: "manual",
                message: "File number was not found in ResWare.",
            });
            return;
        }

        const data = response.data;
        setTransactionData(data);
        setValue("transactionType", data.transactionType);

        setValue(
            "lenderInformation",
            [
                data.lender?.name ?? "Unknown",
                data.lender?.email ?? "",
                data.lender?.phone ?? "",
            ].join("\n")
        );
    };

    const partyType = watch('partyType');
    const isTimeFlexible = watch("isTimeFlexible"); 

    const populateAgentInfo = (type) => {
        if (!transactionData) return setValue('agentInformation', '');

        const agent =
            type === 'buyer' ? transactionData.sellingAgent :
                type === 'seller' ? transactionData.listingAgent :
                    null;

        if (!agent) return setValue('agentInformation', '');

        setValue('agentInformation', [
            agent?.name ?? "Unknown",
            agent?.email ?? "",
            agent?.phone ?? ""
        ].join('\n'));
    };

    useEffect(() => {
        if (partyType === 'buyer' || partyType === 'seller') {
            populateAgentInfo(partyType);
        }
    }, [partyType, transactionData]);

    useEffect(() => {
        if (submitted || pageError) {
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            })
        }
    }, [submitted, pageError]);

    useEffect(() => {
        const dt = getValues("closingDate");
        if (!dt?.isValid) return;

        if (isTimeFlexible) {
            setValue("closingDate", dt.startOf("day"), { shouldValidate: true });
        } else {
            setValue("closingDate", dt.set({ hour: 9, minute: 0, second: 0 }), { shouldValidate: true });
            setValue("timeOptions", "", { shouldDirty: true, shouldValidate: false });
        }
    }, [isTimeFlexible, getValues, setValue]);

    const onSubmit = formData => {
        setPageError(null);
        setSubmitted(false);
        setIsSubmitting(true);

        const { closingDate: dt, ...cleaned } = formData;

        if (!dt?.isValid) {
            setPageError("Please select a valid signing date.");
            return;
        }

        cleaned.closingDate = dt.toISODate();

        if (cleaned.isTimeFlexible) {
            cleaned.closingTime = null;
            cleaned.timeOptions = (cleaned.timeOptions ?? "").trim();
        } else {
            cleaned.closingTime = dt.toFormat("HH:mm:ss");
            cleaned.timeOptions = "";
        }
        var allOffices = regions.flatMap(region => region.offices || []);
        var emails = [allOffices.find(o => o.officeId === cleaned.office).courtesyEmail];
        if (cleaned.office2)
            emails = [...emails, allOffices.find(o => o.officeId === cleaned.office2).courtesyEmail];

        var offices = [allOffices.find(o => o.officeId === cleaned.office).officeName];
        if (cleaned.office2)
            offices = [...offices, allOffices.find(o => o.officeId === cleaned.office2).officeName];

        const requestObject = {
            ...cleaned,
            partyType: cleaned.partyType,
            requestedBy: adminUserProfile.displayName,
            requestedByEmail: adminUserProfile.email,
            selectedOfficeEmails: emails.join(", "),
            selectedOffices: offices.join(", "),
            timeZone,
            selectedAgentInformation: cleaned.agentInformation.replace(/\n/g, "<br>"),
            lenderInformation: cleaned.lenderInformation.replace(/\n/g, "<br>")
        };

        const saveSigningRequest = async () => {
            await apiAdmin.post(`/save-courtesy-signing-request`, requestObject)
                .then((response) => {
                    if (response.data) {
                        reset();
                        setSubmitted(true);
                        setTransactionData(null);
                        setIsSubmitting(false);
                        setShowSecondOffice(false);
                    } else {
                        setPageError("There was an error saving the request. Please try again.");
                    }
                });
        };
        saveSigningRequest();
    };

    return (
        <div>
            <title>Courtesy Signing Request</title>
            <h1>Courtesy Signing Request</h1>
            {pageError && <div className="alert alert-danger" role="alert">{pageError}</div>}
            {submitted && (
                <>
                    <div className="alert alert-success alert-dismissible" role="alert">
                        Your signing request has been submitted successfully!
                    </div>
                </>
            )}
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="border p-3">
                    <div className="row align-items-end mb-3">
                        <div className="col-12 col-md-6">
                            <TextField
                                label="File Number *"
                                {...register("fileNumber", { required: "This field is required" })}
                                className="w-100"
                            />
                            {errors?.fileNumber?.message && (
                                <small className="text-danger">{errors.fileNumber.message}</small>
                            )}
                        </div>

                        <div className="col-12 col-md-4">
                            <button type="button" className="btn btn-primary w-100" onClick={autoFillAddress}>Autofill Info from ResWare</button>
                        </div>
                    </div>
                    <div className="mb-3">
                        <TextField
                            label="Transaction Type *"
                            {...register("transactionType", { required: true })}
                            className="w-100"
                            InputLabelProps={{ shrink: true }}
                        />
                        {errors?.transactionType?.type === "required" && <small className="text-danger">This field is required</small>}
                    </div>
                    <div className="mb-3">
                        <TextField
                            label="Lender Information *"
                            {...register("lenderInformation", { required: true })}
                            className="w-100"
                            multiline
                            minRows={3}
                            InputLabelProps={{ shrink: true }}
                        />
                        {errors?.lenderInformation?.type === "required" && ( <small className="text-danger">This field is required</small>)}
                    </div>
                    <div className="mb-3">
                        <FormControl className="w-100" margin="normal">
                            <InputLabel id="party-type-label">Party</InputLabel>
                            <Controller
                                name="partyType"
                                control={control}
                                defaultValue=""
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        labelId="party-type-label"
                                        label="Party"
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            field.onChange(value);
                                            populateAgentInfo(value);
                                        }}
                                    >
                                        <MenuItem value="buyer">Buyer</MenuItem>
                                        <MenuItem value="seller">Seller</MenuItem>
                                    </Select>
                                )}
                            />
                            {errors?.partyType?.type === 'required' && (<small className="text-danger">Please choose Buyer or Seller</small> )}
                        </FormControl>
                    </div>
                    <div className="mb-3">
                        <Controller
                            name="agentInformation"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <TextField
                                    label="Agent Information *"
                                    className="w-100"
                                    multiline
                                    minRows={3}
                                    inputProps={{ style: { whiteSpace: 'pre-wrap' } }}
                                    InputLabelProps={{ shrink: true }}
                                    {...field}
                                />
                            )}
                        />
                        {errors?.agentInformation?.type === 'required' && (
                            <small className="text-danger">This field is required</small>
                        )}
                    </div>
                    <div className="mb-3">
                        <div className="form-check form-switch">
                            <input className="form-check-input" type="checkbox" defaultChecked={false} id="isTimeFlexible" {...register("isTimeFlexible")} />
                            <label className="form-check-label" htmlFor="isTimeFlexible">Is the signing time flexible?</label>
                        </div>
                    </div>
                    {isTimeFlexible ? (
                        <div className="mb-3 row">
                            <div className="col-md-6 col-6">
                                <Controller
                                    name="closingDate"
                                    control={control}
                                    defaultValue={null}
                                    rules={{ required: "Signing date is required" }}
                                    render={({ field: { onChange, value } }) => (
                                        <DatePicker
                                            value={value}
                                            onChange={onChange}
                                            className="w-100"
                                            label="Signing Date *"
                                        />
                                    )}
                                />
                                {errors?.closingDate && (
                                    <small className="text-danger">{errors.closingDate.message}</small>
                                )}
                            </div>
                            <div className="col-md-6 col-12">
                                <TextField
                                    label="Available Time Range *"
                                    helperText="Examples: 1-4 PM, morning, or after 3 PM"
                                    {...register("timeOptions", {
                                        validate: (v) =>
                                            isTimeFlexible ? (!!v?.trim() || "Time range is required") : true
                                    })}
                                    className="w-100"
                                />
                                {errors?.timeOptions && (
                                    <small className="text-danger">{errors.timeOptions.message}</small>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="mb-3 row">
                            <div className="col-md-6 col-6">
                                <Controller
                                    name="closingDate"
                                    control={control}
                                    defaultValue={null}
                                    rules={{ required: "Signing date/time is required" }}
                                    render={({ field: { onChange, value } }) => (
                                        <DateTimePicker
                                            value={value}
                                            onChange={onChange}
                                            className="w-100"
                                            label="Signing Date/Time *"
                                        />
                                    )}
                                />
                                {errors?.closingDate && (
                                    <small className="text-danger">{errors.closingDate.message}</small>
                                )}
                            </div>
                            <div className="col-md-6 col-12">
                                <ToggleButtonGroup
                                    value={timeZone}
                                    exclusive
                                    onChange={(e, newTimeZone) => { if (newTimeZone) setTimeZone(newTimeZone); }}
                                    aria-label="Time Zone"
                                >
                                        <ToggleButton value="Mountain Time" aria-label="Mountain Time">Mountain Time</ToggleButton>
                                        <ToggleButton value="Pacific Time" aria-label="Pacific Time">Pacific Time</ToggleButton>
                                </ToggleButtonGroup>
                            </div>
                        </div>
                    )}
                    <div className="col-12 mb-3">
                        <TextField
                            label="Special Circumstances"
                            helperText="Examples: deceased, divorced"
                            {...register("specialCircumstances")}
                            className="w-100"
                        />
                    </div>
                    <div className="mb-3">
                        <div className="form-check form-switch">
                            <input className="form-check-input" type="checkbox" defaultChecked={false} id="isSpanishSpeaking" {...register("isSpanishSpeaking")} />
                            <label className="form-check-label" htmlFor="isSpanishSpeaking">Spanish Speaking?</label>
                        </div>
                    </div>
                    <div className="mb-3">
                        <div className="form-check form-switch">
                            <input className="form-check-input" type="checkbox" defaultChecked={false} id="hasFirstAndSecondLoan" {...register("hasFirstAndSecondLoan")} />
                            <label className="form-check-label" htmlFor="hasFirstAndSecondLoan">Is this a 1st and a 2nd?</label>
                        </div>
                    </div>
                    <div className="mb-3">
                        <div className="form-check form-switch">
                            <input className="form-check-input" type="checkbox" defaultChecked={false} id="hasPowerOfAttorney" {...register("hasPowerOfAttorney")} />
                            <label className="form-check-label" htmlFor="hasPowerOfAttorney">POA?</label>
                        </div>
                    </div>
                    <div className="mb-3">
                        <div className="form-check form-switch">
                            <input className="form-check-input" type="checkbox" defaultChecked={false} id="isSameDaySign" {...register("isSameDaySign")} />
                            <label className="form-check-label" htmlFor="isSameDaySign">Is this same day sign/fund?</label>
                        </div>
                    </div>
                    <div className="mb-3">
                        <div className="row">
                            <div className="col-8">
                                <FormControl fullWidth error={!!errors.office}>
                                    <InputLabel id="office-label">Office *</InputLabel>
                                    <Controller
                                        name="office"
                                        control={control}
                                        defaultValue=""
                                        rules={{ required: "Office is required" }}
                                        render={({ field }) => (
                                            <Select
                                                labelId="office-label"
                                                label="Office *"
                                                id="office"
                                                value={field.value ?? ""}
                                                onChange={(e) => field.onChange(e.target.value)}
                                            >
                                                {regions
                                                    .filter(r => r.offices?.some(o => o.courtesyEmail))
                                                    .flatMap(region => [
                                                        <ListSubheader
                                                            key={`${region.regionId ?? region.regionName}-hdr`}
                                                            disableSticky
                                                        >
                                                            {region.regionName}
                                                        </ListSubheader>,
                                                        ...(region.offices ?? [])
                                                            .filter(o => o.courtesyEmail)
                                                            .map(o => (
                                                                <MenuItem key={o.officeId} value={o.officeId}>
                                                                    {o.officeName}
                                                                </MenuItem>
                                                            ))
                                                    ])}
                                            </Select>
                                        )}
                                    />
                                </FormControl>
                                {errors.office && <small className="d-block text-danger">{errors.office.message}</small>}
                                {(!showSecondOffice) && <button type="button" className="btn btn-link" onClick={() => { setShowSecondOffice(true); setValue("office2", null); }}><MdOutlineAddHomeWork title="Add alternate Office option" className="md-react-icons pe-1" />Add alternate office</button>}
                            </div>
                        </div>
                    </div>
                    {showSecondOffice && 
                    <div className="mb-3">
                        <div className="row">
                            <div className="col-8">
                                <FormControl fullWidth error={!!errors.office2}>
                                    <InputLabel id="office2-label">Alternate Office</InputLabel>
                                    <Controller
                                        name="office2"
                                        control={control}
                                        defaultValue=""
                                        rules={{ validate: (value) => value !== getValues("office") || "Select a different office than already selected, or remove the alternate office." }}
                                        render={({ field }) => (
                                            <div className="d-flex justify-content-center">
                                                <Select
                                                    labelId="office2-label"
                                                    id="office"
                                                    value={field.value ?? ""}
                                                    label="Alternate Office"
                                                    onChange={(e) => field.onChange(e.target.value)}
                                                    className="flex-fill"                                                    
                                                >
                                                    {regions
                                                        .filter(r => r.offices?.some(o => o.courtesyEmail))
                                                        .flatMap(region => [
                                                            <ListSubheader
                                                                key={`${region.regionId ?? region.regionName}-hdr`}
                                                                disableSticky
                                                            >
                                                                {region.regionName}
                                                            </ListSubheader>,
                                                            ...(region.offices ?? [])
                                                                .filter(o => o.courtesyEmail)
                                                                .map(o => (
                                                                    <MenuItem key={o.officeId} value={o.officeId}>
                                                                        {o.officeName}
                                                                    </MenuItem>
                                                                ))
                                                        ])}
                                                </Select>
                                                <button type="button" className="btn btn-link ms-3" onClick={() => { setShowSecondOffice(false) }}><MdOutlineRemoveCircleOutline title="Remove Alternate Office" className="md-react-icons" /></button>
                                            </div>
                                        )}
                                        />                                        
                                </FormControl>                                
                                {errors.office2 && <small className="d-block text-danger">{errors.office2.message}</small>}
                            </div>
                        </div>
                    </div>
                    }
                    <div className="col-12 mb-3">
                        <TextField
                            label="Escrow CC Email"
                            {...register("escrowCCEmails")}
                            className="w-100"
                            helperText="The requester will be CC'd on all email replies. If you would like anyone else to see the email replies (team emails, EOs) add their emails to this section separated by commas."
                        />
                    </div>
                    <div className="mb-3">
                        <button type="submit" disabled={isSubmitting} className="btn btn-primary w-auto me-3">{isSubmitting ? <><span className="spinner-border spinner-border-sm" aria-hidden="true"></span><span role="status">Submitting...</span></> : "Submit Request"}</button>

                    </div>
                </div>
            </form>
        </div>
    );
};

export default CourtesySigningRequest;
